import { useState, useRef, useEffect } from 'react';
import { apiClient } from '../api';

// Suggested questions shown when chat drawer is first opened
const SUGGESTIONS = {
  waiter: [
    'How do I open a table?',
    'How do I add items to an order?',
    'How do I void a line?',
    'How do I request the bill?',
  ],
  cashier: [
    'How do I view all open tickets?',
    'How do I process an M-Pesa payment?',
    'How do I close a ticket?',
  ],
  manager: [
    'How do I reset a staff PIN?',
    'How do I void an entire ticket?',
    'How do I change a menu item price?',
  ],
  owner: [
    'How do I add a new menu item?',
    'How do I set up happy hour pricing?',
    'How do I reconcile M-Pesa payments?',
    'How do I configure role permissions?',
  ],
};

export default function AiHelpButton ({ currentScreen, role }) {
  const [open,     setOpen]     = useState(false);
  const [messages, setMessages] = useState([]);
  const [input,    setInput]    = useState('');
  const [loading,  setLoading]  = useState(false);
  const bottomRef               = useRef(null);
  const inputRef                = useRef(null);

  const suggestions = SUGGESTIONS[role] || SUGGESTIONS.waiter;

  // Scroll to bottom whenever messages update
  useEffect(() => {
    if (open) {
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 80);
    }
  }, [messages, open]);

  // Auto-focus input when drawer opens
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 200);
  }, [open]);

  async function send (text) {
    const trimmed = text?.trim();
    if (!trimmed || loading) return;

    setInput('');
    const userMsg = { role: 'user', content: trimmed };
    const updated = [...messages, userMsg];
    setMessages(updated);
    setLoading(true);

    try {
      const { data: { reply: assistantReply } } = await apiClient.post('/ai/guidance', {
        message: trimmed,
        screen:  currentScreen || '',
        history: updated.slice(-6),
      });

      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: assistantReply },
      ]);
    } catch {
      setMessages(prev => [
        ...prev,
        {
          role:    'assistant',
          content: "Sorry, I'm having trouble right now. Please try again in a moment.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  }

  function handleKey (e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send(input);
    }
  }

  function clearChat () {
    setMessages([]);
    setInput('');
  }

  return (
    <>
      {/* ── Floating trigger button ─────────────────────────────────────── */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          title="ServeIQ Assistant"
          style={{
            position:       'fixed',
            bottom:         24,
            right:          24,
            zIndex:         900,
            width:          52,
            height:         52,
            borderRadius:   '50%',
            background:     '#ff7a00',
            border:         'none',
            cursor:         'pointer',
            fontSize:       22,
            boxShadow:      '0 4px 16px rgba(0,0,0,0.45)',
            display:        'flex',
            alignItems:     'center',
            justifyContent: 'center',
            transition:     'transform 0.15s',
          }}
          onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.1)')}
          onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
        >
          🤖
        </button>
      )}

      {/* ── Bottom drawer ───────────────────────────────────────────────── */}
      {open && (
        <div
          style={{
            position:      'fixed',
            bottom:        0,
            left:          0,
            right:         0,
            zIndex:        1000,
            height:        '72vh',
            background:    '#1c1c1c',
            borderRadius:  '18px 18px 0 0',
            display:       'flex',
            flexDirection: 'column',
            boxShadow:     '0 -6px 32px rgba(0,0,0,0.6)',
          }}
        >
          {/* Header */}
          <div
            style={{
              padding:        '14px 18px',
              borderBottom:   '1px solid #2e2e2e',
              display:        'flex',
              justifyContent: 'space-between',
              alignItems:     'center',
              flexShrink:     0,
            }}
          >
            <div>
              <div
                style={{
                  color:      '#fff',
                  fontWeight: 700,
                  fontSize:   15,
                  fontFamily: 'Barlow Condensed, sans-serif',
                }}
              >
                🤖 ServeIQ Assistant
              </div>
              {currentScreen && (
                <div style={{ color: '#555', fontSize: 11, marginTop: 2 }}>
                  📍 {currentScreen} screen
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              {messages.length > 0 && (
                <button
                  onClick={clearChat}
                  style={{
                    background: 'none',
                    border:     'none',
                    color:      '#555',
                    fontSize:   11,
                    cursor:     'pointer',
                  }}
                >
                  Clear
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                style={{
                  background: 'none',
                  border:     'none',
                  color:      '#666',
                  fontSize:   22,
                  cursor:     'pointer',
                  lineHeight: 1,
                }}
              >
                ✕
              </button>
            </div>
          </div>

          {/* Message list */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '14px 16px' }}>

            {/* Empty state — suggestion chips */}
            {messages.length === 0 && (
              <div>
                <p style={{ color: '#555', fontSize: 12, marginBottom: 12 }}>
                  Ask me anything about using ServeIQ:
                </p>
                {suggestions.map(q => (
                  <button
                    key={q}
                    onClick={() => send(q)}
                    style={{
                      display:      'block',
                      width:        '100%',
                      textAlign:    'left',
                      background:   '#242424',
                      border:       '1px solid #333',
                      color:        '#bbb',
                      padding:      '10px 14px',
                      borderRadius: 8,
                      marginBottom: 8,
                      cursor:       'pointer',
                      fontSize:     13,
                      lineHeight:   1.4,
                    }}
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}

            {/* Conversation messages */}
            {messages.map((m, idx) => (
              <div
                key={idx}
                style={{
                  display:        'flex',
                  justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start',
                  marginBottom:   10,
                }}
              >
                <div
                  style={{
                    maxWidth:     '82%',
                    background:   m.role === 'user' ? '#ff7a00' : '#2a2a2a',
                    color:        '#fff',
                    padding:      '10px 14px',
                    borderRadius: m.role === 'user'
                      ? '12px 12px 2px 12px'
                      : '12px 12px 12px 2px',
                    fontSize:     13,
                    lineHeight:   1.55,
                  }}
                >
                  {m.content}
                </div>
              </div>
            ))}

            {/* Thinking indicator */}
            {loading && (
              <div
                style={{
                  display:        'flex',
                  justifyContent: 'flex-start',
                  marginBottom:   10,
                }}
              >
                <div
                  style={{
                    background:   '#2a2a2a',
                    color:        '#666',
                    padding:      '10px 14px',
                    borderRadius: '12px 12px 12px 2px',
                    fontSize:     12,
                  }}
                >
                  Thinking…
                </div>
              </div>
            )}

            <div ref={bottomRef} />
          </div>

          {/* Input bar */}
          <div
            style={{
              padding:    '10px 14px',
              borderTop:  '1px solid #2e2e2e',
              display:    'flex',
              gap:        8,
              flexShrink: 0,
            }}
          >
            <input
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Ask a question…"
              disabled={loading}
              style={{
                flex:         1,
                background:   '#242424',
                border:       '1px solid #333',
                color:        '#fff',
                padding:      '10px 14px',
                borderRadius: 8,
                fontSize:     14,
                outline:      'none',
              }}
            />
            <button
              onClick={() => send(input)}
              disabled={loading || !input.trim()}
              style={{
                background:   '#ff7a00',
                border:       'none',
                color:        '#fff',
                padding:      '10px 20px',
                borderRadius: 8,
                fontWeight:   700,
                fontSize:     14,
                cursor:       loading || !input.trim() ? 'not-allowed' : 'pointer',
                opacity:      loading || !input.trim() ? 0.45 : 1,
              }}
            >
              Send
            </button>
          </div>
        </div>
      )}
    </>
  );
}
